import { LoginCredentials, User, Product, Category, ApiResponse, SignupData, ShippingType, RestaurantProfile, RestaurantProfileResponse, Feedback, FeedbackListResponse, FeedbackCreateResponse, Staff, StaffRole, StaffStatus, FoodSearchImage } from '../types';
import { Order } from '../types';
import { notificationService } from './notificationService';

// Global error handler for 401 errors
let onUnauthorized: (() => void) | null = null;

export const setUnauthorizedHandler = (handler: () => void) => {
  onUnauthorized = handler;
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';
const UNAUTHORIZED_RETRY_DELAY_MS = 1000;
const LOGIN_ENDPOINT = '/shopOwnerTable/login';

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const shouldHandleUnauthorized = (endpoint: string) => endpoint !== LOGIN_ENDPOINT;

const persistAccessTokenFromResponse = (
  response: Response,
  setToken: (token: string) => void
): void => {
  const headerToken = response.headers.get('x-access-token');
  if (headerToken) {
    setToken(headerToken);
    localStorage.setItem('auth_token', headerToken);
  }
};

const notifySessionExpired = (): void => {
  clearStoredAuthCredentials();
  alert('Your session has expired. Please login again.');
  onUnauthorized?.();
};

/** Cookie names backends often use for refresh sessions (camelCase + snake_case). */
const REFRESH_TOKEN_COOKIE_NAMES = ['refreshToken', 'refresh_token'] as const;

/**
 * Expire refresh-token cookies the SPA can touch (non-HttpOnly).
 * HttpOnly cookies are invisible to JS — the backend must clear them via Set-Cookie on logout if needed.
 */
function clearRefreshTokenCookies(): void {
  if (typeof document === 'undefined') return;
  const expire = 'Thu, 01 Jan 1970 00:00:01 GMT';
  const host = window.location.hostname || '';

  try {
    for (const name of REFRESH_TOKEN_COOKIE_NAMES) {
      document.cookie = `${name}=;expires=${expire};path=/`;
      document.cookie = `${name}=;Max-Age=0;path=/`;
      document.cookie = `${name}=;expires=${expire};path=/;SameSite=Lax`;

      if (window.location.protocol === 'https:') {
        document.cookie = `${name}=;expires=${expire};path=/;Secure`;
        document.cookie = `${name}=;expires=${expire};path=/;SameSite=None;Secure`;
      }

      if (host && host !== 'localhost') {
        document.cookie = `${name}=;expires=${expire};path=/;domain=${host}`;
        const segments = host.split('.');
        if (segments.length >= 2) {
          const root = segments.slice(-2).join('.');
          document.cookie = `${name}=;expires=${expire};path=/;domain=.${root}`;
        }
      }
    }
  } catch {
    /* ignore */
  }
}

/** Drop auth-related keys from local/session storage and refresh-token cookies (best-effort). */
export function clearStoredAuthCredentials(): void {
  const keys = ['auth_token', 'refreshToken', 'refresh_token'] as const;
  try {
    for (const key of keys) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  } catch {
    /* ignore private mode / quota */
  }
  clearRefreshTokenCookies();
  try {
    notificationService.clearFcmToken();
  } catch {
    /* ignore */
  }
}

class ApiService {
  private token: string | null | undefined = null;

  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  /** Fetch with optional 401 retry (1s delay once) before session expiry / logout. */
  private async executeAuthenticatedFetch(
    url: string,
    options: RequestInit,
    endpoint: string
  ): Promise<Response> {
    const run = () =>
      fetch(url, {
        ...options,
        credentials: 'include',
      });

    let response = await run();
    persistAccessTokenFromResponse(response, (token) => {
      this.token = token;
    });

    if (response.status !== 401 || !shouldHandleUnauthorized(endpoint)) {
      return response;
    }

    await delay(UNAUTHORIZED_RETRY_DELAY_MS);
    response = await run();
    persistAccessTokenFromResponse(response, (token) => {
      this.token = token;
    });

    if (response.status === 401) {
      this.token = null;
      notifySessionExpired();
    }

    return response;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
      ...options.headers,
    };

    try {
      const response = await this.executeAuthenticatedFetch(
        url,
        { ...options, headers },
        endpoint
      );

      if (response.status === 401 && shouldHandleUnauthorized(endpoint)) {
        return {
          status: false,
          message: 'Unauthorized. Please login again.',
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        status: false,
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  }
  

  async login(credentials: LoginCredentials): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.makeRequest<{ user: User; token: string }>('/shopOwnerTable/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.status) {
      this.token = response.token || "";
      localStorage.setItem('auth_token', this.token);
    }

    return response;
  }

  async signup(signupData: SignupData): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.makeRequest<{ user: User; token: string }>('/shopTable/update', {
      method: 'PATCH',
      body: JSON.stringify(signupData),
    });
    return response;
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    return this.makeRequest<User>('/api/auth/current-user');
  }

  async sendOtp(mobile: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.makeRequest<{ message: string }>('/shopOwnerTable/signup', {
      method: 'POST',
      body: JSON.stringify({ mobile, shopOwnerType:"shopOwner"}),
    });
    if (response.status && response.doc) {
      alert(response.doc.mobileOtp);
    }
    return response;
  }
  async resendOtp(mobile: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.makeRequest<{ message: string }>('/shopOwnerTable/generateOtp', {
      method: 'POST',
      body: JSON.stringify({ mobile}),
    });
    if (response.status && response.mobileOtp) {
      alert(response.mobileOtp);
    }
    return response;
  }
  async resetPassword(mobile: string, mobileOtp: string, password: string): Promise<ApiResponse<{ message: string }>> {
    return this.makeRequest<{ message: string }>('/shopOwnerTable/updatePassword', {
      method: 'POST',
      body: JSON.stringify({ mobile, mobileOtp, password }),
    });
  }

  async verifyOtp(mobile: string, otp: string): Promise<ApiResponse<{ verified: boolean }>> {
    const response = await this.makeRequest<{ verified: boolean }>('/shopOwnerTable/verifyOtp', {
      method: 'POST',
      body: JSON.stringify({ mobile, mobileOtp:otp }),
    });
    if (response.status) {
      this.token = response.token || "";
      localStorage.setItem('auth_token', this.token);
    }
    return response;
  }

  async logout(): Promise<void> {
    const currentToken = this.token || localStorage.getItem('auth_token');

    try {
      await fetch(`${API_BASE_URL}/shopOwnerTable/logout`, {
        method: 'POST',
        headers: {
          ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
        },
        credentials: 'include',
      });
    } catch {
      // Even if backend logout fails, clear client-side credentials.
    } finally {
      this.token = null;
      clearStoredAuthCredentials();
    }
  }

  async createProduct(product: Omit<Product, '_id'>): Promise<ApiResponse<Product>> {
    return this.makeRequest<Product>('/productTable', {
      method: 'POST',
      body: JSON.stringify(product),
    });
  }

  async getProducts(): Promise<ApiResponse<Product[]>> {
    return this.makeRequest<Product[]>('/productTable?pageNumber=1&itemsPerPage=100',{
      method: 'GET',
    });
  }

  async getProductsPaginated(page: number, limit: number, searchText?: string, category?: string): Promise<ApiResponse<Product[]>> {
    const pageNumber = Math.max(1, page);
    const itemsPerPage = Math.max(1, limit);
    let url = `/productTable?pageNumber=${pageNumber}&itemsPerPage=${itemsPerPage}`;
    
    if (searchText && searchText.trim()) {
      url += `&searchText=${encodeURIComponent(searchText.trim())}`;
    }
    if (category && category.trim()) {
      url += `&category=${encodeURIComponent(category.trim())}`;
    }
    
    return this.makeRequest<Product[]>(url, {
      method: 'GET',
    });
  }

  async updateProduct(id: string, product: Partial<Product>): Promise<ApiResponse<Product>> {
    void id;
    const unsafeProduct = product as any;
    delete unsafeProduct.status;
    delete unsafeProduct.createdBy;
    delete unsafeProduct.createdAt;
    delete unsafeProduct.shopId;
    delete unsafeProduct.lastUpdatedAt;
    delete unsafeProduct.uniqueProductId;
    delete unsafeProduct.__v;
    delete unsafeProduct.manufactureDoc;
    delete unsafeProduct.defaultImage;
    delete unsafeProduct.lastUpdatedBy;
    return this.makeRequest<Product>(`/productTable`, {
      method: 'PATCH',
      body: JSON.stringify(unsafeProduct),
    });
  }

  async deleteProduct(id: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/productTable/${id}`, {
      method: 'DELETE',
    });
  }

  async createCategory(category: Omit<Category, '_id'>): Promise<ApiResponse<Category>> {
    return this.makeRequest<Category>('/categoryTable', {
      method: 'POST',
      body: JSON.stringify(category),
    });
  }

async getCategories(): Promise<ApiResponse<Category[]>> {
  return this.makeRequest<Category[]>('/categoryTable', {
    method: 'GET',
  });
}


async updateCategory(id: string, category: Partial<Category>): Promise<ApiResponse<Category>> {
  // Attach ID to category object
  category._id = id;

  return this.makeRequest<Category>(`/categoryTable`, {
    method: 'PATCH',           // change method to PATCH
    body: JSON.stringify(category),
  });
}

async deleteCategory(id: string): Promise<ApiResponse<void>> {
  return this.makeRequest<void>(`/categoryTable/${id}`, {
    method: 'DELETE',
  });
}
async deleteTable(id: string): Promise<ApiResponse<void>> {
  return this.makeRequest<void>(`/tableTable/${id}`, {
    method: 'DELETE',
  });
}


  async getOrders(): Promise<ApiResponse<Order[]>> {
    return this.makeRequest<Order[]>('/orders');
  }

  async getOrdersPaginated(
    page: number,
    limit: number,
    startDate?: string,
    endDate?: string,
    status?: string | string[],
    table?: string
  ): Promise<any> {
    let url = `/orderTable/orders_list?page=${page}&limit=${limit}`;
    
    if (startDate) {
      url += `&start=${startDate}`;
    }
    
    if (endDate) {
      url += `&end=${endDate}`;
    }
    
    if (status !== undefined && status !== '') {
      const statuses = Array.isArray(status) ? status : [status];
      for (const s of statuses) {
        const v = typeof s === 'string' ? s.trim() : '';
        if (v) url += `&status=${encodeURIComponent(v)}`;
      }
    }
    
    if (table) {
      url += `&table=${encodeURIComponent(table)}`;
    }
    
    return this.makeRequest<any>(url, {
      method: 'GET',
    });
  }

  async getOrdersByCustomer(customerId: string, paymentStatus?: string): Promise<any> {
    let url = `/orderTable/orders_list?page=1&limit=1000&customer=${encodeURIComponent(customerId)}`;

    if (paymentStatus !== undefined) {
      url += `&paymentStatus=${encodeURIComponent(paymentStatus)}`;
    }
    
    return this.makeRequest<any>(url, {
      method: 'GET',
    });
  }

  async updateOrderStatus(
    orderId: string,
    status: string,
    comment?: string,
    extraData: Record<string, any> = {}
  ): Promise<any> {
    return this.makeRequest<any>('/orderTable/updateByShopOwner', {
      method: 'PATCH',
      body: JSON.stringify({
        _id: orderId,
        status: status,
        comment: comment || '',
        ...extraData
      }),
    });
  }

  async updateOrderPayment(
    orderId: string,
    paymentData: {
      paymentMethod: string;
      paymentStatus: string;
      referenceId: string;
      paymentNote?: string;
    }
  ): Promise<any> {
    return this.makeRequest<any>('/orderTable/updateByShopOwner', {
      method: 'PATCH',
      body: JSON.stringify({
        _id: orderId,
        ...paymentData
      }),
    });
  }

  async createPaymentLink(payload: {
    referenceId: string;
    from: string;
    totalAmount: number;
  }): Promise<any> {
    return this.makeRequest<any>('/orderTable/createPaymentLink', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getTables(): Promise<any> {
    return this.makeRequest<any>('/tableTable', {
      method: 'GET',
    });
  }

  async getTableSessions(): Promise<any> {
    return this.makeRequest<any>('/tableTable', {
      method: 'GET',
    });
  }

  async createTable(name: string): Promise<any> {
    return this.makeRequest<any>('/tableTable', {
      method: 'POST',
      body: JSON.stringify({ 
        name: name
      }),
    });
  }

  async updateTable(id: string, name: string, status: string): Promise<any> {
    return this.makeRequest<any>('/tableTable', {
      method: 'PATCH',
      body: JSON.stringify({ 
        _id: id,
        name: name,
        status: status
      }),
    });
  }

  async updateSessionStatus(sessionId: string, status: string): Promise<any> {
    return this.makeRequest<any>('/tableTable/remove', {
      method: 'PATCH',
      body: JSON.stringify({ 
        _id: sessionId
      }),
    });
  }

  async rejectOrder(id: string, reason: string): Promise<ApiResponse<Order>> {
    return this.makeRequest<Order>(`/orders/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  }

  async getShippingTypes(): Promise<ApiResponse<{ doc: ShippingType[] }>> {
    return this.makeRequest<{ doc: ShippingType[] }>('/shippingTypeTable/list', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async uploadImage(file: File, category: string = 'product'): Promise<ApiResponse<{ path: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', category);

    const url = `${API_BASE_URL}/imageTable`;
    const headers = {
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
    };

    try {
      const response = await this.executeAuthenticatedFetch(
        url,
        {
          method: 'POST',
          headers,
          body: formData,
        },
        '/imageTable'
      );

      if (response.status === 401) {
        return {
          status: false,
          message: 'Unauthorized. Please login again.',
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        status: false,
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

async getImages(category: string = 'productImage'): Promise<ApiResponse<{ path: string }[]>> {
  return this.makeRequest<{ path: string }[]>(`/imageTable?category=${category}`, {
    method: 'GET'
  });
}

  async searchFoodImages(
    name: string,
    options?: { orientation?: string }
  ): Promise<ApiResponse<FoodSearchImage[]>> {
    const params = new URLSearchParams({ name: name.trim() });
    if (options?.orientation?.trim()) {
      params.set('orientation', options.orientation.trim());
    }
    return this.makeRequest<FoodSearchImage[]>(`/imageTable/search-food?${params.toString()}`, {
      method: 'GET',
    });
  }


  async deleteImage(path: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.makeRequest<{ success: boolean }>(`/imageTable/${encodeURIComponent(path)}`, {
      method: 'DELETE',
    });
  }

// Restaurant Profile API methods
async getRestaurantProfile(): Promise<RestaurantProfileResponse> {
  return this.makeRequest<RestaurantProfile>('/shopTable', {
    method: 'GET',
  });
}


  async updateRestaurantProfile(profile: RestaurantProfile): Promise<ApiResponse<{ status: boolean }>> {
    // Only send fields that are actually defined (not undefined)
    const updateData: any = {};
    
    if (profile.name !== undefined) updateData.name = profile.name;
    if (profile.id !== undefined) updateData.id = profile.id;
    if (profile.address !== undefined) updateData.address = profile.address;
    if (profile.description !== undefined) updateData.description = profile.description;
    if (profile.email !== undefined) updateData.email = profile.email;
    if (profile.your_name !== undefined) updateData.your_name = profile.your_name;
    if ((profile as any).phone !== undefined) updateData.phone = (profile as any).phone;
    if (profile.hours !== undefined) updateData.hours = profile.hours;
    if (profile.logo !== undefined) updateData.logo = profile.logo;
    if (profile.coverImage !== undefined) updateData.coverImage = profile.coverImage;
    if (profile.socialMedia !== undefined) updateData.socialMedia = profile.socialMedia;
    if (profile.mapurl !== undefined) updateData.mapurl = profile.mapurl;
    if (profile.enable_table_ordering !== undefined) updateData.enable_table_ordering = profile.enable_table_ordering;
    if (profile.enable_whatsapp_ordering !== undefined) updateData.enable_whatsapp_ordering = profile.enable_whatsapp_ordering;
    if (profile.auto_remove_user !== undefined) updateData.auto_remove_user = profile.auto_remove_user;
    if (profile.remove_user_time_in_hour !== undefined) updateData.remove_user_time_in_hour = profile.remove_user_time_in_hour;
    if (profile.remove_user_time_in_minute !== undefined) updateData.remove_user_time_in_minute = profile.remove_user_time_in_minute;
    if (profile.enable_left_table !== undefined) updateData.enable_left_table = profile.enable_left_table;
    if (profile.enable_notification !== undefined) updateData.enable_notification = profile.enable_notification;
    if (profile.enable_help !== undefined) updateData.enable_help = profile.enable_help;
    if (profile.hide_price !== undefined) updateData.hide_price = profile.hide_price;
    if (profile.address_enabled !== undefined) updateData.address_enabled = profile.address_enabled;
    if (profile.payment_option !== undefined) updateData.payment_option = profile.payment_option;
    if (profile.razorpay_key_id !== undefined) updateData.razorpay_key_id = profile.razorpay_key_id;
    if (profile.razorpay_key_secret !== undefined) updateData.razorpay_key_secret = profile.razorpay_key_secret;
    if (profile.upi_id !== undefined) updateData.upi_id = profile.upi_id;
    if (profile.is_prepaid !== undefined) updateData.is_prepaid = profile.is_prepaid;
    if (profile.stop_auto_payment_link !== undefined) updateData.stop_auto_payment_link = profile.stop_auto_payment_link;
    if (profile.theme !== undefined) updateData.theme = profile.theme;

    return this.makeRequest<{ status: boolean }>('/shopTable', {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
  }

  async updateRestaurantProfileSEO(seoData: {
    var_title: string;
    var_description: string;
    var_keywords: string;
    var_og_description: string;
    var_site_name: string;
    var_twitter_description: string;
    var_og_image_url?: string;
  }): Promise<ApiResponse<{ status: boolean }>> {
    // Send only the seo key with the SEO data
    const updateData = {
      seo: seoData
    };

    return this.makeRequest<{ status: boolean }>('/shopTable', {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
  }

  // Feedback API methods
  async getFeedbacks(): Promise<FeedbackListResponse> {
    return this.makeRequest<Feedback[]>('/reviewTable', {
      method: 'GET',
    }) as Promise<FeedbackListResponse>;
  }

  async submitFeedback(review: string, images: string[] = []): Promise<FeedbackCreateResponse> {
    return this.makeRequest<Feedback>('/reviewTable', {
      method: 'POST',
      body: JSON.stringify({
        review: review,
        images: images
      }),
    }) as Promise<FeedbackCreateResponse>;
  }

  async replyToFeedback(feedbackId: string, reply: string): Promise<ApiResponse<{ status: boolean }>> {
    return this.makeRequest<{ status: boolean }>('/reviewTable/reply', {
      method: 'POST',
      body: JSON.stringify({
        _id: feedbackId,
        reply: reply
      }),
    });
  }

  // Alternative reply method in case the endpoint structure is different
  async replyToFeedbackAlternative(feedbackId: string, reply: string): Promise<ApiResponse<{ status: boolean }>> {
    return this.makeRequest<{ status: boolean }>('/reviewTable/update', {
      method: 'POST',
      body: JSON.stringify({
        _id: feedbackId,
        replies: [reply] // Some APIs might expect an array
      }),
    });
  }

  // Analytics API method
  async getAnalytics(startDate: string, endDate: string): Promise<{
    success: boolean;
    analytics: Array<{ status: string; value: number }>;
    settings: { products: number; categories: number; tables: number };
  }> {
    const start = encodeURIComponent(startDate);
    const end = encodeURIComponent(endDate);
    
    console.log('API Service: Making analytics request to:', `/orderTable/analytics?start=${start}&end=${end}`);
    
    const response = await this.makeRequest<any>(`/orderTable/analytics?start=${start}&end=${end}`, {
      method: 'GET',
    });
    
    console.log('API Service: Analytics response:', response);
    
    // The analytics API returns data directly in the response format
    // Check if we have the expected structure
    if (response.success !== undefined) {
      // Direct response format from analytics API
      return {
        success: response.success,
        analytics: response.analytics || [],
        settings: response.settings || { products: 0, categories: 0, tables: 0 }
      };
    } else if (response.status) {
      // Wrapped response format from makeRequest
      return {
        success: true,
        analytics: response.analytics || [],
        settings: response.settings || { products: 0, categories: 0, tables: 0 }
      };
    } else {
      return {
        success: false,
        analytics: [],
        settings: { products: 0, categories: 0, tables: 0 }
      };
    }
  }

  // Plans API method
  async getPlans(): Promise<ApiResponse<{
    count: number;
    plans: Array<{
      id: string;
      name: string;
      amount: number;
    }>;
  }> & {
    count?: number;
    plans?: Array<{
      id: string;
      name: string;
      amount: number;
    }>;
  }> {
    const response = await this.makeRequest<{
      count: number;
      plans: Array<{
        id: string;
        name: string;
        amount: number;
      }>;
    }>('/shopTable/plans', {
      method: 'GET',
    });
    
    // The API returns the data directly in the response, not nested
    return response as any;
  }

  // Create Razorpay subscription
  async createSubscription(planId: string): Promise<ApiResponse<{
    status: boolean;
    subscription_id: string;
    customer_id: string;
  }>> {
    return this.makeRequest<{
      status: boolean;
      subscription_id: string;
      customer_id: string;
    }>('/shopTable/create-subscription', {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId }),
    });
  }

  // Cancel subscription
  async cancelSubscription(): Promise<ApiResponse<{
    status: boolean;
    message: string;
    razorpay_subscription_cancelled?: boolean;
  }>> {
    return this.makeRequest<{
      status: boolean;
      message: string;
      razorpay_subscription_cancelled?: boolean;
    }>('/shopTable/cancel-subscription', {
      method: 'POST',
    });
  }

  // Verify subscription payment
  async verifySubscriptionPayment(paymentData: {
    razorpay_payment_id?: string;
    razorpay_subscription_id?: string;
    razorpay_signature?: string;
  }): Promise<ApiResponse<{ status: boolean }>> {
    return this.makeRequest<{ status: boolean }>('/shopTable/verify-subscription-payment', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  // Enable trial mode
  async enableTrial(): Promise<ApiResponse<{ status: boolean }>> {
    return this.makeRequest<{ status: boolean }>('/shopTable/trial', {
      method: 'PATCH',
      body: JSON.stringify({ trial_mode: true }),
    });
  }

  async getStaffs(status: StaffStatus = 'active'): Promise<ApiResponse<Staff[]>> {
    return this.makeRequest<Staff[]>(`/shopOwnerTable/member?status=${encodeURIComponent(status)}`, {
      method: 'GET',
    });
  }

  async createStaff(payload: {
    name: string;
    mobile: string;
    shopOwnerType: StaffRole;
    wa_enabled?: boolean;
  }): Promise<ApiResponse<Staff>> {
    return this.makeRequest<Staff>('/shopOwnerTable/member', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateStaff(payload: {
    id: string;
    name: string;
    mobile: string;
    shopOwnerType: StaffRole;
    status: StaffStatus;
    wa_enabled?: boolean;
  }): Promise<ApiResponse<Staff>> {
    return this.makeRequest<Staff>('/shopOwnerTable/member', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }
}

export const apiService = new ApiService();